import React from 'react'
import classNames from 'classnames'
import Stars from 'Components/stars'
import { Image, smallLogoLoading } from 'Components/view-image'
import formatNumber from '@arsenal/arsenal/modules/formatNumber'
import xss from 'xss'
const yellow_star_little = require('static/yellow-star-little.png')
const black_star_little = require('static/black-star-little.png')

interface IWrapperProps {
  index: number
  className?: string
  onClick?(): void
}

const Wrapper: React.FC<IWrapperProps> = ({
  index,
  className,
  onClick,
  children,
}) => {
  return (
    <div
      className={classNames(
        'tw-border-1 md:tw-border-l-0 md:tw-border-r-0 md:tw-border-b-0 tw-border-common-cyan-blue tw-relative tw-text-12 xl:tw-text-14 tw-pt-16 tw-pb-12 md:tw-pb-8 tw-px-16 md:tw-px-0',
        {
          'md:tw-border-t-0': !index,
          'md:tw-pt-12': index,
        },
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  )
}

interface IBottomText {
  text: string
  icon?: string
  iconUrl?: string
}
interface IBottomProps {
  texts: IBottomText[]
}

const Bottom: React.FC<IBottomProps> = ({ texts }) => {
  return (
    <div className="tw-mt-2 tw-text-video-shallow-gray tw-flex tw-items-center tw-h-20 tw-leading-20 ">
      {texts.map(({ text, icon, iconUrl }, index) => {
        return (
          <p
            className="tw-whitespace-nowrap tw-mr-8 xl:tw-mr-20 line-1"
            key={index}
          >
            {iconUrl ? (
              <span className="tw-inline-block tw-pl-16 lg:tw-pl-19 tw-bg-14 lg:tw-bg-16 icon">
                {text}
              </span>
            ) : (
              <>
                <i
                  className={classNames(
                    'DCD_Icon tw-align-bottom tw-text-12 lg:tw-text-14 tw-mr-4',
                    icon
                  )}
                />
                <span>{text}</span>
              </>
            )}
            <style jsx>
              {`
                .icon {
                  background-image: url('${iconUrl}');
                  background-repeat: no-repeat;
                  background-position: left center;
                }
              `}
            </style>
          </p>
        )
      })}
    </div>
  )
}

interface IHotTopicItemProps extends IWrapperProps {
  name?: string
  desc?: string
  coverUrl?: string
  readCount?: number
  joinCount?: number
}

export const HotTopicItem: React.FC<IHotTopicItemProps> = (props) => {
  const { index, name, desc, coverUrl, readCount, joinCount, onClick } = props

  return (
    <Wrapper
      index={index}
      onClick={onClick}
      className="tw-flex tw-items-center"
    >
      <div className="tw-flex-none tw-mr-10 tw-h-80">
        <Image
          width={80}
          height={80}
          src={coverUrl || ''}
          className="tw-rounded-2"
          hoverTransition
          errorImg={smallLogoLoading}
          cusDefaultImg={smallLogoLoading}
        />
      </div>
      <div className="tw-overflow-hidden">
        <p className="tw-font-semibold tw-text-common-black tw-leading-20 line-1">{`# ${name}`}</p>
        <div
          className="tw-mt-2 line-2 tw-text-common-gray hover:tw-text-common-orange tw-leading-20"
          dangerouslySetInnerHTML={{
            __html: xss.filterXSS(desc || ''),
          }}
        />
        <Bottom
          texts={[
            {
              text: `${formatNumber(joinCount || 0)}人参与`,
              icon: 'icon_comment_24',
            },
            { text: `${formatNumber(readCount || 0)}阅读` },
          ]}
        />
      </div>
    </Wrapper>
  )
}

interface IHotItemProps extends IWrapperProps {
  hasImage?: boolean
  hasVideo?: boolean
  content?: string
  score?: number
  scoreLevel?: string
  commentCount?: number
  commentText: string
  diggCount?: number
  carName?: string
  year?: string
  price?: string
  location?: string
  questionInfo?: {
    status: number
    status_display: string
    send_award: string
    award_icon_url: string
  }
}

// ugc 内容
const HotItem: React.FC<IHotItemProps> = ({
  hasImage,
  hasVideo,
  index,
  content,
  year,
  carName,
  score,
  scoreLevel,
  commentCount,
  commentText,
  diggCount,
  price,
  location,
  questionInfo,
  onClick,
}) => {
  const bottomTexts: IBottomText[] = [
    {
      text: `${commentCount || 0}${commentText}`,
      icon: 'icon_comment_24',
    },
  ]
  if (commentText === '回答') {
    const { status, status_display, send_award, award_icon_url } =
      questionInfo || {}
    if (status === 1) {
      bottomTexts[1] = { text: status_display || '已解决' }
    } else if (send_award && award_icon_url) {
      bottomTexts[1] = { text: send_award, iconUrl: award_icon_url }
    }
  } else {
    bottomTexts[1] = {
      text: `${diggCount || 0}点赞`,
      icon: 'icon_like_24_normal',
    }
  }
  return content ? (
    <Wrapper
      index={index}
      onClick={onClick}
      className="tw-h-full tw-flex tw-flex-col"
    >
      <div className="tw-flex-auto tw-z-10">
        {year && carName && scoreLevel ? (
          <div className="tw-p-12 tw-bg-video-light-gray tw-rounded-2 tw-mb-10">
            <p
              className="tw-truncate tw-leading-22 tw-text-12 md:tw-text-14 xl:tw-text-16 tw-font-medium"
              title={`${year}款 ${carName}`}
            >
              {`${year}款 ${carName}`}
            </p>
            <div className="tw-truncate tw-leading-20 tw-mt-12 tw-flex tw-items-center">
              <span className="tw-text-common-gray">车主评分：</span>
              <span className="tw-font-medium">
                {`${((Number(score) || 0) / 100).toFixed(2)}分`}
              </span>
              <div className="tw-ml-6 md:tw-hidden xl:tw-block">
                <Stars
                  starIcon={yellow_star_little}
                  starVacantIcon={black_star_little}
                  score={Number(score) / 100}
                  maxScore={5}
                  starWidth={13}
                  starHeight={11}
                />
              </div>
              <span className="tw-ml-6 tw-font-medium">{`“${scoreLevel}”`}</span>
            </div>
            {price || location ? (
              <div className="tw-flex tw-flex-wrap">
                {price ? (
                  <p className="tw-mt-12 tw-mr-20">
                    <span className="tw-text-common-gray">裸车价：</span>
                    <span className="tw-font-medium">{price}万</span>
                  </p>
                ) : null}
                {location ? (
                  <p className="tw-mt-12">
                    <span className="tw-text-common-gray">购车地：</span>
                    <span className="tw-font-medium">{location}</span>
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        <p className="tw-flex-auto line-2 hover:tw-text-common-orange tw-leading-18 lg:tw-leading-22">
          {hasVideo ? (
            <i
              className={`DCD_Icon icon_play_2 tw-align-bottom tw-mr-4 tw-text-14 lg:tw-text-16`}
              style={{ color: '#5c5cff' }}
            />
          ) : hasImage ? (
            <i
              className={`DCD_Icon icon_image_24 tw-align-bottom tw-mr-4 tw-text-14 lg:tw-text-16`}
              style={{ color: '#5c5cff' }}
            />
          ) : null}
          {content}
        </p>
      </div>
      <Bottom texts={bottomTexts} />
    </Wrapper>
  ) : null
}

export default HotItem
